import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from './translatable-text.model';

export const DEPARTMENT_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type DepartmentTranslatableField = (typeof DEPARTMENT_TRANSLATABLE_FIELDS)[number];
export type DepartmentTranslations = TranslatableText<DepartmentTranslatableField>;

export const departmentQuerySchema = type({
  'name?': 'string',
});

export type DepartmentQuery = typeof departmentQuerySchema.t;

export const createDepartmentSchema = type({
  name: type('string').configure({
    description: 'Display name of the department.',
    examples: ['Operations', 'Care Team A'],
  }),
  'description?': type('string | null').configure({
    description: 'Free-form description. `null` clears the value.',
  }),
  'translations?': translatableTextSchema(DEPARTMENT_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({
  description: 'Payload for creating a new department.',
  examples: [{ name: 'Operations', translations: { en: { name: 'Operations', description: null }, de: { name: 'Betrieb' } } }],
});

export type CreateDepartment = typeof createDepartmentSchema.t;

export const updateDepartmentSchema = type({
  'name?': type('string').configure({
    description: 'Updated display name.',
    examples: ['Operations'],
  }),
  'description?': type('string | null').configure({
    description: 'Updated description. `null` clears the value.',
  }),
  'translations?': translatableTextSchema(DEPARTMENT_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({
  description: 'Partial update of a department — fields omitted from the body remain unchanged.',
  examples: [{ name: 'Operations (updated)', translations: { de: { name: 'Operationen' } } }],
});

export type UpdateDepartment = typeof updateDepartmentSchema.t;

export interface DepartmentResponse {
  id: string;
  name: string;
  description: string | null;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
